import Compressor from 'browser-image-compression';
import logger from './logger';
/**
 * Compresses an image file to be under the specified max size
 * @param {File} imageFile - The image file to compress
 * @param {number} maxSizeMB - Maximum size in MB (default: 1)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(imageFile, maxSizeMB = 0.4) {
  console.group('Image Compression');
  logger.debug('Starting compression for:', imageFile.name);
  
  // Check if it's an image file
  if (!imageFile.type.startsWith('image/')) {
    logger.debug('File is not an image, skipping compression');
    console.groupEnd();
    return imageFile;
  }
  
  // Log initial details
  logger.debug(`File details:
  - Type: ${imageFile.type}
  - Size: ${(imageFile.size / 1024 / 1024).toFixed(2)} MB
  - Last modified: ${new Date(imageFile.lastModified).toLocaleString()}`);
  
  // Create a temporary URL to get image dimensions
  const originalUrl = URL.createObjectURL(imageFile);
  
  try {
    // Get original dimensions
    const originalDimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.src = originalUrl;
    });
    
    logger.debug(`Original dimensions: ${originalDimensions.width}x${originalDimensions.height}`);
    
    // Set compression options
    const options = {
      maxSizeMB: maxSizeMB,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: imageFile.type,
      onProgress: (percent) => {
        logger.debug(`Compression progress: ${percent.toFixed(0)}%`);
      },
    };
    
    logger.debug('Compression settings:', options);
    const startTime = performance.now();
    
    // Compress the image
    const compressedFile = await Compressor(imageFile, options);
    
    const endTime = performance.now();
    const compressionTime = ((endTime - startTime) / 1000).toFixed(2);
    const compressionRatio = (imageFile.size / compressedFile.size).toFixed(2);
    
    // Get compressed dimensions
    const compressedUrl = URL.createObjectURL(compressedFile);
    const compressedDimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.src = compressedUrl;
    });
    
    logger.debug(`Compression results:
    - Original size: ${(imageFile.size / 1024 / 1024).toFixed(2)} MB
    - Compressed size: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB
    - Compression ratio: ${compressionRatio}x
    - Original dimensions: ${originalDimensions.width}x${originalDimensions.height}
    - Compressed dimensions: ${compressedDimensions.width}x${compressedDimensions.height}
    - Processing time: ${compressionTime} seconds`);
    
    console.groupEnd();
    return compressedFile;
  } catch (error) {
    logger.error('Error compressing image:', error);
    console.groupEnd();
    URL.revokeObjectURL(originalUrl); // Clean up in case of error
    return imageFile; // Return original if compression fails
  }
} 